// src/hooks/useLocationLabels.js
import { useEffect, useState, useCallback } from 'react';
import api from '../api/api';
import { ENDPOINTS } from '../api/endpoints';

/**
 * Parses a concatenated audition-location string into an array of codes.
 * If the 3rd character is a digit, takes 3 chars; otherwise 2.
 */
function parseLocationCodes(str) {
  str = str || "";

  const codes = [];
  let i = 0;
  while (i < str.length) {
    const third = str.charAt(i + 2);
    if (third && /\d/.test(third)) {
      codes.push(str.substr(i, 3));
      i += 3;
    } else {
      codes.push(str.substr(i, 2));
      i += 2;
    }
  }
  return codes;
}

/**
 * Custom hook to fetch location labels and provide a formatter.
 * Returns an object with:
 *  - loading: boolean
 *  - error: any
 *  - formatLocations: (codeString: string) => string
 */
export function useLocationLabels() {
  const [labelMap, setLabelMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    async function fetchLabels() {
      try {
        // use shared api instance and centralized endpoint
        const response = await api.get(ENDPOINTS.getLocations());
        const all = response.data?.data || [];
        const map = {};
        all.forEach(({ locationCode, locationState, locationCity }) => {
          const code = locationCode?.trim();
          const state = locationState?.trim();
          const city = locationCity?.trim();
          if (code && state) {
            map[code] = city && city !== state
              ? `${state} (${city})`
              : state;
          }
        });
        if (mounted) {
          setLabelMap(map);
          setLoading(false);
        }
      } catch (err) {
        console.error("Failed to fetch location labels:", err);
        if (mounted) {
          setError(err);
          setLoading(false);
        }
      }
    }
    fetchLabels();
    return () => { mounted = false; };
  }, []);

  const formatLocations = useCallback(
    (codeString) => {
      const codes = parseLocationCodes(codeString);
      return codes
        .map((c) => labelMap[c] || c)
        .join(', ');
    },
    [labelMap]
  );

  return { loading, error, formatLocations };
}
